import {Component, Inject, OnInit, Optional} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {filter} from 'rxjs';
import {Departments} from 'src/app/enums/departments';
import {IPromotionModal} from 'src/app/interfaces/hr-data.interface';
import {HrDataService} from 'src/app/services/employee-details/hr-data.service';
import {Utils} from 'src/app/shared/utils/utils';
import {IPositionNewEmployee} from '../../../../interfaces/dropdown-new-employee.interface';
import {DIALOG_DATA, DialogService} from '../../../../services/dialog.service';
import {DialogRef} from '../../../../shared/ui-kits/dialog/dialog-ref';
import {LinkDocModalComponent} from '../../../../shared/ui-kits/table/link-doc-modal/link-doc-modal.component';

@UntilDestroy()
@Component({
  selector: 'app-promotion',
  templateUrl: './promotion.component.html',
  styleUrls: ['./promotion.component.scss'],
})
export class PromotionComponent implements OnInit {
  public promotionForm!: UntypedFormGroup;
  public isCreate = false;
  public isDatePickerOpened!: boolean;
  public isDepartmentSelected: boolean = false;
  public availablePositions: IPositionNewEmployee[] = [];
  private deliveryId!: number;
  private deliverySubIds: number[] = [];
  public isTechStackVisible: boolean = false;
  public validateAllFormFields = Utils.validateAllFormFields;
  public isFieldInvalid = Utils.isFieldInvalid;

  constructor(
    private dialogRef: DialogRef,
    @Optional() @Inject(DIALOG_DATA) public data: IPromotionModal,
    private dialogService: DialogService,
    private hrDataService: HrDataService
  ) {}

  ngOnInit() {
    if (!this.data.value.id) {
      this.isCreate = true;
    }
    this.promotionForm = new UntypedFormGroup({
      comment: new UntypedFormControl(this.data.value.comment, [
        Validators.maxLength(400),
      ]),
      date: new UntypedFormControl(this.data.value.date, [Validators.required]),
      employeeId: new UntypedFormControl(this.data.value.employeeId),
      id: new UntypedFormControl(this.data.value.id),
      level: new UntypedFormControl(this.data.value.level, [
        Validators.required,
      ]),
      department: new UntypedFormControl(this.data.value.department, [
        Validators.required,
      ]),
      subDepartment: new UntypedFormControl(this.data.value.subDepartment),
      position: new UntypedFormControl(this.data.value.position, [
        Validators.required,
      ]),
      role: new UntypedFormControl(this.data.value.role),
      stackTechnology: new UntypedFormControl(this.data.value.stackTechnology),
      status: new UntypedFormControl(this.data.value.status, [
        Validators.required,
      ]),
      pm: new UntypedFormControl(this.data.value.pm, [Validators.required]),
      hr: new UntypedFormControl(this.data.value.hr, [Validators.required]),
      hrId: new UntypedFormControl(
        this.data.value.hr?.name ? this.data.value.hr?.id : null
      ),
      pmId: new UntypedFormControl(
        this.data.value.pm?.name ? this.data.value.pm?.id : null
      ),
      file: new UntypedFormControl(this.data.value.file),
    });

    this.promotionForm
      .get('pm')
      ?.valueChanges.pipe(
        untilDestroyed(this),
        filter(v => !!v)
      )
      .subscribe(value => {
        this.promotionForm.get('pmId')?.setValue(value.id);
      });

    this.promotionForm
      .get('hr')
      ?.valueChanges.pipe(
        untilDestroyed(this),
        filter(v => !!v)
      )
      .subscribe(value => {
        this.promotionForm.get('hrId')?.setValue(value.id);
      });

    this.setDeliveryDepIds();

    if (!this.isCreate) {
      this.setCorrectInitialData();
    }
  }

  setDeliveryDepIds() {
    this.data.dropdowns.Department.forEach(el => {
      if (el.name === Departments.Delivery) {
        this.deliveryId = +el.id;
        this.deliverySubIds.push(+el.id);
      }
    });
    this.data.dropdowns.SubDepartment.forEach(el =>
      this.deliverySubIds.push(+el.id)
    );
  }

  setCorrectInitialData(): void {
    const dropdownControls = [
      'level',
      'department',
      'subDepartment',
      'position',
      'stackTechnology',
      'status',
      'pm',
      'hr',
    ];
    for (const name of dropdownControls) {
      if (!this.promotionForm.controls[name].value?.name) {
        this.promotionForm.controls[name].setValue(null);
      }
    }

    if (this.data.value.department) {
      this.getPositions();
    }
    if (this.data.value.position) {
      this.checkPositionRequirements();
    }
  }

  getPositions() {
    const departmentId = this.promotionForm.get('department')?.value?.id;
    const subDepartmentValue = this.promotionForm.get('subDepartment')?.value;
    this.availablePositions = this.data.dropdowns.Positions.filter(
      (item: IPositionNewEmployee) =>
        departmentId === this.deliveryId
          ? subDepartmentValue
            ? item.department.id === subDepartmentValue?.id
            : this.deliverySubIds.includes(+item.department.id)
          : departmentId === item.department.id
    );
    this.isDepartmentSelected = true;
  }

  setSubDepartmentValidation() {
    if (this.isDeliveryDepartment) {
      Utils.enableControl('subDepartment', this.promotionForm);
      this.promotionForm.controls['subDepartment'].setValidators([
        Validators.required,
      ]);
    } else {
      Utils.disableControl('subDepartment', this.promotionForm);
    }
  }

  checkPositionRequirements() {
    this.isTechStackVisible = false;
    for (const item of this.availablePositions) {
      if (item.id === this.promotionForm.get('position')?.value?.id) {
        if (item.requiresTechStack) {
          this.isTechStackVisible = item.requiresTechStack;
        }
      }
    }
  }

  onDepartmentChanged(): void {
    this.getPositions();
    this.setSubDepartmentValidation();
    this.promotionForm.get('subDepartment')?.setValue(null);
    this.promotionForm.get('position')?.setValue(null);
  }

  onSubDepartmentChanged(): void {
    this.getPositions();
    this.promotionForm.get('position')?.setValue(null);
  }

  attachLink(): void {
    this.dialogService
      .open(LinkDocModalComponent, {
        data: null,
        settings: {
          width: '450px',
          height: '250px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(res => !!res)
      )
      .subscribe(result => {
        this.promotionForm.get('file')?.setValue(result);
      });
  }

  deleteDocLink(): void {
    this.promotionForm.controls['file'].setValue('');
  }

  saveData() {
    if (this.promotionForm.invalid) {
      this.validateAllFormFields(this.promotionForm);
      return;
    }
    this.dialogRef.close(this.promotionForm.value);
  }

  cancelSave() {
    this.dialogRef.close();
  }

  deletePromotion() {
    this.hrDataService
      .deletePromotion(this.data.value.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.dialogRef.close('deleted');
      });
  }

  get subDepartment() {
    return this.data.dropdowns.SubDepartment;
  }

  get isDeliveryDepartment() {
    const departmentId = this.promotionForm.get('department')?.value?.id;
    return Boolean(departmentId === 6);
  }
}
